import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  InputSignal,
  model,
  ModelSignal,
  OnInit,
  output,
  OutputEmitterRef,
  Signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatList, MatListItem, MatListItemTitle } from '@angular/material/list';
import type { OrderHistoryItemModel } from '@libs/services/models';
import { LuxonDatePipe } from '@libs/shared/ui';
import { ProductLineItemComponent, ProductLineItemPriceOverride, ProductNotOrderableComponent } from '@libs/shop/components';
import { ProductListItemBaseModel, ProductModel } from '@libs/shop/models';

@Component({
  selector: 'ui-order-history-item',
  templateUrl: './order-history-item.component.html',
  styleUrls: ['./order-history-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LuxonDatePipe,
    MatList,
    MatListItem,
    MatListItemTitle,
    NgTemplateOutlet,
    ProductLineItemComponent,
    ProductNotOrderableComponent,
  ],
})
export class OrderHistoryItemComponent implements OnInit {
  public orderItem: InputSignal<OrderHistoryItemModel> = input.required();
  public position: InputSignal<number> = input(0);
  public canReorder: InputSignal<boolean> = input(true);
  public showCheckbox: InputSignal<boolean> = input(false);
  public selected: ModelSignal<boolean> = model(false);

  public quantityChanged: OutputEmitterRef<number> = output();

  public orderItemProductName: Signal<string> = computed(() => this.orderItem().productData?.details?.name ?? this.orderItem().name);
  public isUnavailable: Signal<boolean> = computed(() => this.orderItem().amountDelivered === 0);
  public isPartiallyAvailable: Signal<boolean> = computed(() => {
    const orderItem: OrderHistoryItemModel = this.orderItem();

    return orderItem.amountDelivered > 0 && orderItem.amountDelivered < orderItem.amountOrdered;
  });

  public productItem: Signal<ProductListItemBaseModel | undefined> = computed(() => {
    const productData: ProductModel | undefined = this.orderItem().productData;

    if (!productData) {
      return undefined;
    }

    return {
      sku: this.orderItem().sku,
      quantity: this.orderItem().amountOrdered,
      productData,
    };
  });

  public priceOverride: Signal<ProductLineItemPriceOverride | undefined> = computed(() => {
    if (this.canReorder()) {
      return undefined;
    }

    const orderItem: OrderHistoryItemModel = this.orderItem();

    return {
      prices: {
        priceType: 'default',
        singleUnit: orderItem.prices.priceUnit * 1000,
        salesUnit: orderItem.prices.priceSalesUnit * 1000,
        salesUnitDeposit: orderItem.deposit && orderItem.deposit.prices.priceSalesUnit * 1000,
        endUserPriceLabel: 'UVP',
        endUser: 0,
        isFree: orderItem.isFree,
        redacted: orderItem.prices.redacted,
      },
      total: orderItem.prices.price * 1000,
      deposit: (orderItem.deposit?.prices.price ?? 0) * 1000,
    };
  });

  public quantityCounterControl: FormControl<number> = new FormControl(0, { updateOn: 'blur' }) as FormControl<number>;

  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    this.quantityCounterControl.setValue(this.orderItem().amountOrdered);
    this.quantityCounterControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((quantity: number) => void this.quantityChanged.emit(quantity));
  }
}
